import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import Icon from 'components/AppIcon';
import { toast } from 'sonner';

const ToastNotification = ({ 
  notification, 
  onClose, 
  onReply, 
  onMarkAsRead, 
  onView,
  position = 'top-right' 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [timeLeft, setTimeLeft] = useState(5000); // 5 seconds default

  useEffect(() => {
    if (!isHovered && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(prev => prev - 100);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [timeLeft, isHovered]);

  useEffect(() => {
    if (timeLeft <= 0 && !isHovered) {
      handleClose();
    }
  }, [timeLeft, isHovered]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 300);
  };

  const handleReply = () => {
    onReply(notification);
    handleClose();
  };

  const handleView = () => {
    onView(notification);
    handleClose();
  };

  const handleMarkAsRead = () => {
    onMarkAsRead(notification.id);
    handleClose();
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'text-red-400 bg-red-500/20';
      case 'writer': return 'text-green-400 bg-green-500/20';
      case 'qa': return 'text-blue-400 bg-blue-500/20';
      case 'client': return 'text-purple-400 bg-purple-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return 'Shield';
      case 'writer': return 'PenTool';
      case 'qa': return 'CheckCircle';
      case 'client': return 'User';
      default: return 'User';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'comment': return 'MessageSquare';
      case 'assignment': return 'FileText';
      case 'deadline': return 'Clock';
      case 'status': return 'Bell';
      default: return 'Bell';
    }
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'top-right': return 'fixed top-4 right-4 z-[9000]';
      case 'top-left': return 'fixed top-4 left-4 z-[9000]';
      case 'bottom-right': return 'fixed bottom-4 right-4 z-[9000]';
      case 'bottom-left': return 'fixed bottom-4 left-4 z-[9000]';
      default: return 'fixed top-4 right-4 z-[9000]';
    }
  };

  const slideVariants = {
    initial: {
      x: position.includes('right') ? 400 : -400,
      opacity: 0,
      scale: 0.8
    },
    animate: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25
      }
    },
    exit: {
      x: position.includes('right') ? 400 : -400,
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <div className={getPositionClasses()}>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial="initial"
            animate="animate"
            exit="exit"
            variants={slideVariants}
            className="w-96 max-w-sm"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-2xl overflow-hidden">
              {/* Progress Bar */}
              <div className="w-full h-1 bg-white/10">
                <motion.div
                  className="h-full bg-gradient-to-r from-primary to-secondary"
                  initial={{ width: '100%' }}
                  animate={{ width: `${(timeLeft / 5000) * 100}%` }}
                  transition={{ duration: 0.1 }}
                />
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start space-x-3">
                  {/* Notification Icon */}
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon 
                      name={getNotificationIcon(notification.type)} 
                      size={16} 
                      className="text-primary" 
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${getRoleColor(notification.author_role)}`}>
                        <Icon 
                          name={getRoleIcon(notification.author_role)} 
                          size={12} 
                        />
                      </div>
                      <span className="text-white font-medium text-sm truncate">
                        {notification.author_name}
                      </span>
                      <span className="text-white/50 text-xs">
                        {format(new Date(notification.created_at), 'HH:mm')}
                      </span>
                    </div>

                    <p className="text-white text-sm mb-2 line-clamp-2">
                      {notification.type === 'comment' ? 
                        `${notification.parent_id ? 'Replied to your comment' : 'Commented on your assignment'}` :
                        notification.content
                      }
                    </p>

                    {notification.content && (
                      <p className="text-white/70 text-xs line-clamp-2 mb-3">
                        {notification.content}
                      </p>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={handleReply}
                        className="bg-primary/20 hover:bg-primary/30 text-primary px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center space-x-1"
                      >
                        <Icon name="Reply" size={12} />
                        <span>Reply</span>
                      </button>
                      <button
                        onClick={handleView}
                        className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center space-x-1"
                      >
                        <Icon name="Eye" size={12} />
                        <span>View</span>
                      </button>
                      <button
                        onClick={handleMarkAsRead}
                        className="bg-secondary/20 hover:bg-secondary/30 text-secondary px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center space-x-1"
                      >
                        <Icon name="Check" size={12} />
                        <span>Mark Read</span>
                      </button>
                    </div>
                  </div>

                  {/* Close Button */}
                  <button
                    onClick={handleClose}
                    className="text-white/60 hover:text-white transition-colors"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ToastNotification;